// EQ-1-GYM-COLDSTART — the athlete's DECLARED absences, and what they imply.
//
// The rule this module exists to hold (equipment-model.md §11.6, Antenore
// 2026-08-17): "X is here" is derived from the log, "Y is NOT here" is
// declared and never inferred. A swap is ambiguous — the machine was busy,
// the knee hurt, the programme said so — so reading absence out of it would
// mint a fact nobody stated. Everything here operates on facts the athlete
// ticked in the ✕ dialog, and nothing in this file derives one.
//
// Pure, RN/web-portable, no DDB import — same contract as coachReadProjection
// and equipmentInstances, whose vocabulary this extends.

import type { Profile } from "./schemas/profile.js";
import type { EquipmentInstanceSurvey } from "./equipmentInstances.js";

export type LocationUnavailable = NonNullable<Profile["locationUnavailable"]>;
export type LocationUnavailableEntry = LocationUnavailable[number];

/** What the athlete declared missing for one exercise at one place. */
export const unavailableAt = (
  list: LocationUnavailable | undefined,
  place: string,
  exercise: string,
): LocationUnavailableEntry | undefined =>
  list?.find((e) => e.place === place && e.exercise === exercise);

/**
 * Is this specific instance declared missing here?
 *
 * `instance === null` asks about the virtual Default (EQ-1-DEFAULT), which has
 * no slug by construction and therefore rides `unnamed` rather than the list.
 */
export const isInstanceUnavailable = (
  list: LocationUnavailable | undefined,
  place: string,
  exercise: string,
  instance: string | null,
): boolean => {
  const entry = unavailableAt(list, place, exercise);
  if (!entry) return false;
  return instance === null
    ? entry.unnamed === true
    : (entry.instances?.includes(instance) ?? false);
};

/**
 * Does anything remain to train this exercise with, here?
 *
 * ⛔ This is the ONLY thing that may sink a row, and it is deliberately strict:
 * declaring ONE machine missing does not mean the exercise is impossible. A
 * gym with two leg presses, one of them gone, still has a leg press. The row
 * sinks only when every instance the survey knows about has been declared
 * missing — otherwise the athlete would lose an exercise they can still do.
 *
 * ⚠️ Reads the survey, never the raw log: the survey is where the virtual
 * Default is materialised, and an exercise with no named machines has exactly
 * one instance (that one). Today that is EVERY exercise — equipment-instance
 * tags in production are zero — so in practice one tick sinks the row, which
 * is the behaviour Antenore described. That is an outcome of the data, not a
 * rule written into the code, and it stops being true on its own the day a
 * second machine gets named.
 */
export const hasAvailableInstance = (
  survey: EquipmentInstanceSurvey,
  list: LocationUnavailable | undefined,
  place: string,
  exercise: string,
): boolean => {
  const entry = unavailableAt(list, place, exercise);
  if (!entry) return true;
  if (survey.unnamed && entry.unnamed !== true) return true;
  return survey.named.some((i) => !(entry.instances?.includes(i.value) ?? false));
};

/**
 * The named machines the athlete declared missing here — the values, resolved.
 *
 * ⭐ Why this exists next to `isInstanceUnavailable` rather than inside its
 * callers: two different paths have to narrow by the same fact — what the
 * machine tray OFFERS, and what the add/replace paths APPLY IN SILENCE. If
 * they answered differently, a machine could be un-offerable in the picker and
 * still get stamped on an entry with no tap at all, which is the worst of both
 * (invisible, and wrong). Same reason `getSilentInstanceForSlug` exists at all.
 *
 * `place === null` narrows NOTHING: with no place there is no key, so there is
 * no declaration to read (EQ-1-PLACEID is what turns the key into an id).
 *
 * ⚠️ Routed through `isInstanceUnavailable` even though every value in
 * `entry.instances` trivially satisfies it. The predicate is the one place
 * "is this one absent?" is answered, and reading the field raw here would be a
 * second answer waiting to disagree with it.
 *
 * ⛔ The virtual Default is NOT in this list, and cannot be: it has no value to
 * return. That is not an oversight — see `availableInstancesAt`.
 */
export const declaredAbsentInstancesAt = (
  list: LocationUnavailable | undefined,
  place: string | null,
  exercise: string,
): readonly string[] => {
  if (place === null) return [];
  const entry = unavailableAt(list, place, exercise);
  if (!entry?.instances) return [];
  return entry.instances.filter((value) =>
    isInstanceUnavailable(list, place, exercise, value),
  );
};

/**
 * The machines still choosable, given what was declared absent here.
 *
 * ⛔ It takes VALUES, not a place. The narrowing a declaration performs is a
 * FACT the athlete stated; the narrowing a place would perform is an
 * inference, and that one was deliberately removed (see
 * `getSilentInstanceForSlug`). Keeping the place out of this signature is what
 * stops the two from being confused for each other later.
 *
 * ⛔ It narrows NAMED machines only. The virtual Default is never removed by
 * anything, because choosing it stamps NOTHING on the set — there is no
 * identity to get wrong, and it is the only way back from a tag to no tag
 * (EQ-1-DEFAULT was a P0 precisely because naming a machine had become a
 * one-way door). A declaration can withhold an identity; it cannot withhold
 * the absence of one.
 */
export const availableInstancesAt = <T extends { value: string }>(
  named: readonly T[],
  absentHere: readonly string[],
): T[] =>
  absentHere.length === 0
    ? [...named]
    : named.filter((i) => !absentHere.includes(i.value));

/**
 * Record a declaration, replacing any earlier one for the same place+exercise.
 *
 * Replaces rather than merges: the dialog always shows the CURRENT state with
 * every instance listed, so what comes back is the athlete's whole answer for
 * that pair, not a delta. Merging would make un-ticking impossible.
 *
 * An answer that ticks nothing REMOVES the entry instead of storing an empty
 * one — an entry that declares nothing is indistinguishable from silence, and
 * keeping it would grow the array with rows that mean nothing.
 */
export const declareUnavailable = (
  list: LocationUnavailable | undefined,
  args: {
    place: string;
    exercise: string;
    instances?: string[];
    unnamed?: boolean;
    at: string;
  },
): LocationUnavailable => {
  const rest = (list ?? []).filter(
    (e) => !(e.place === args.place && e.exercise === args.exercise),
  );
  const named = args.instances ?? [];
  if (named.length === 0 && args.unnamed !== true) return rest;
  return [
    ...rest,
    {
      place: args.place,
      exercise: args.exercise,
      ...(named.length > 0 ? { instances: named } : {}),
      ...(args.unnamed === true ? { unnamed: true } : {}),
      at: args.at,
    },
  ];
};

/**
 * The ✚ on a sunk row: undo the whole declaration for this place+exercise.
 *
 * Whole, not per-instance, because the ✚ answers the question the sunk row
 * asks — "this is not here" — and that question has one answer. Per-instance
 * correction is what re-opening the ✕ dialog is for.
 */
export const restoreAvailable = (
  list: LocationUnavailable | undefined,
  place: string,
  exercise: string,
): LocationUnavailable =>
  (list ?? []).filter(
    (e) => !(e.place === place && e.exercise === exercise),
  );
